import { IAgentStatusDeprecatedService } from '@agent/core/agent/agent-status-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ChannelDeprecatedImpl } from '@commons/models/channel';
import { IContact } from '@commons/models/contact';
import { ILogService, IPromise, IQService } from 'angular';
import { filter } from 'rxjs';

import {
  ActivityFacade,
  ActivityStatus,
  ActivityType,
  AgentSkillFacade,
  AgentStatus,
  ChanEnum,
  Configuration,
  None,
  Option,
} from '@contact/upgrade';

const MODULE_NAME = 'circuit';

export interface ICircuitService {
  logon(): IPromise<any>;

  logout(): IPromise<any>;

  acceptCall(): void;

  endCall(): void;

  getCallInProgress(): boolean;

  getCall(): Option<any>;
}

export class CircuitService implements ICircuitService {
  static $inject: Array<string> = [
    '$window',
    '$q',
    '$log',
    'agentStatusDeprecatedService',
    'activityFacade',
    'configuration',
    'toastDeprecatedService',
    'contactDeprecatedService',
    'agentSkillFacade',
  ];

  public name = MODULE_NAME;

  private client: any;
  private localUser: any;
  private call: Option<any> = None;
  private callInProgress: boolean = false;
  private currentStatus: string = 'AVAILABLE';
  private connectionLost: boolean = false;
  private inProgress: boolean = false;

  constructor(
    private readonly $window: any,
    private readonly $q: IQService,
    private readonly $log: ILogService,
    private readonly agentStatusDeprecatedService: IAgentStatusDeprecatedService,
    private readonly activityFacade: ActivityFacade,
    private readonly configuration: Configuration,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly agentSkillFacade: AgentSkillFacade
  ) {
    /**
     * listen to pause state success
     */
    this.activityFacade.success$
      .pipe(
        // if type is CIRCUIT
        filter(({ activity }) => activity.type === ActivityType.CIRCUIT),
        // if status is CANCELED
        filter(({ current }) => current === ActivityStatus.CANCELED)
      )
      .subscribe(({ id }) => {
        this.activityFacade.updateTransition(id, ActivityStatus.FINISHED);
        this.$log.debug('[osv] pause finished');
      });

    // CIRCUIT is in progress
    this.activityFacade.isTypeInprogress(ActivityType.CIRCUIT).subscribe(inProgress => (this.inProgress = inProgress));
  }

  public getCall(): Option<any> {
    return this.call;
  }

  public getCallInProgress(): boolean {
    return this.callInProgress;
  }

  // logon to Unify
  public logon(): IPromise<any> {
    let deferred = this.$q.defer();

    if (this.client) {
      deferred.reject();
    } else {
      this.$log.debug(
        '[circuit] logon domain = ' + this.configuration.circuit?.domain + ' clientId = ' + this.configuration.circuit?.clientId
      );

      this.client = new this.$window.Circuit.Client({
        domain: this.configuration.circuit?.domain,
        client_id: this.configuration.circuit?.clientId,
        scope: 'ALL',
      });

      this.addClientEventListeners();

      this.client
        .logon()
        .then(
          user => {
            this.localUser = user;
            this.$log.info('[circuit] logon success');
            this.setInitialState();
            deferred.resolve();
          },
          () => {
            this.$log.error('[circuit] logon error');
            this.client.removeAllListeners();
            this.client = null;
            deferred.reject();
          }
        )
        .catch(e => {
          this.$log.error('[circuit] Unable to logon. Error: ' + e);
          this.client.removeAllListeners();
          deferred.reject();
        });
    }
    return deferred.promise;
  }

  // logout from Unify
  public logout(): IPromise<any> {
    let deferred = this.$q.defer();
    if (!this.client) {
      deferred.reject();
    } else {
      this.localUser = null;
      this.client.logout();
      this.client.removeAllListeners();
      this.client = null;
      deferred.resolve();
    }

    return deferred.promise;
  }

  public setCall(_call) {
    this.call = Option.of(_call);
  }

  public resetCall(_call) {
    this.call = None;
  }

  // Accept Circuit call
  public acceptCall(): void {
    this.call.forEach(call => {
      let mediaType: any = { audio: true, video: false };
      this.client
        .answerCall(call.callId, mediaType)
        .then(() => {
          this.$log.debug('[circuit] Call has been answered');
          this.callInProgress = true;
        })
        .catch(err => alert('[circuit] Failed to answer call. ' + err));
    });
  }

  public endCall(): void {
    this.call.forEach(call => {
      this.client
        .endCall(call.callId)
        .then(() => {
          this.$log.debug('Successfully ended call');
          this.callInProgress = false;
          this.call = None;
        })
        .catch(err => alert('[circuit] Failed to endCall call. ' + err));
    });
  }

  private setInitialState(): void {
    this.client.getPresence([this.localUser.userId]).then(
      presence => {
        this.$log.debug('[circuit] Initial state : ' + presence[0].state);
        this.updateAgentState(presence[0].state);
      },
      () => {
        this.$log.error('[circuit] Cannot set initial state');
      }
    );
  }

  private addClientEventListeners(): void {
    if (this.client) {
      this.client.addEventListener('connectionStateChanged', evt => {
        this.$log.debug('[circuit] Received connectionStateChanged event - state = ' + evt.state);

        let enabledVoiceSkills: boolean | null = null;
        // If user is connected, set presence in available state
        if (evt.state === 'Connected') {
          this.$log.debug('[circuit] set available');
          enabledVoiceSkills = true;
          if (this.connectionLost) {
            this.toastDeprecatedService.notify('agent:toast.circuit_login_reconnect', 5000);
            this.connectionLost = false;
          }
        } else if (evt.state !== 'Connecting') {
          this.toastDeprecatedService.alert('agent:toast.circuit_login_failed');
          this.connectionLost = true;
          enabledVoiceSkills = false;
        }
        if (enabledVoiceSkills !== null) {
          this.agentSkillFacade.changeAllActivation(ChanEnum.In.Phone, enabledVoiceSkills);
          this.agentSkillFacade.changeAllActivation(ChanEnum.Out.Phone, enabledVoiceSkills);
        }
      });

      // Call state management
      this.client.addEventListener('callStatus', evt => {
        this.call.forEach(call => {
          if (call && call.callId === evt.call.callId) {
            this.$log.debug('[circuit] Received callStatus event - call id = ' + evt.call.callId + ' with state = ' + evt.call.state);
            this.setCall(evt.call);
          }
        });
      });

      // A new call is presented from Circuit
      this.client.addEventListener('callIncoming', evt => {
        this.$log.debug('[circuit] Received callIncoming event - call id = ' + evt.call.callId + ' with state = ' + evt.call.state);
        this.setCall(evt.call);
      });

      // Notification of call end
      this.client.addEventListener('callEnded', evt => {
        this.call.forEach(call => {
          if (call && call.callId === evt.call.callId) {
            this.$log.debug('[circuit] Received callEnded event - call id = ' + evt.call.callId + ' with state = ' + evt.call.state);
            this.resetCall(evt.call);
          }
        });
      });

      // Presence notifications
      this.client.addEventListener('userPresenceChanged', evt => {
        this.$log.debug('[circuit] userPresenceChanged=', evt.presenceState.state);
        this.updateAgentState(evt.presenceState.state);
      });
    }
  }

  private updateAgentState(futureStatus: string): void {
    let currentStatusContact = this.agentStatusDeprecatedService.getCurrentAgentState();
    if (this.currentStatus === 'AVAILABLE' && (futureStatus === 'BUSY' || futureStatus === 'DND' || futureStatus === 'AWAY')) {
      // status is "do not disturb" means pause or "busy" means on call
      // Set agent in pause if he's available
      this.activityFacade.openForm(ActivityType.CIRCUIT);
    }

    // Manage transfer from Circuit
    if (futureStatus === 'AVAILABLE' && currentStatusContact === AgentStatus.WORKING && !this.inProgress) {
      let contacts: IContact[] = this.contactDeprecatedService.getContactsByChannel(ChannelDeprecatedImpl.INCOMING_PHONE);
      if (contacts && contacts.length > 1) {
        this.$log.error("[circuit] Impossible de couper l'appel");
      } else {
        this.contactDeprecatedService.close(contacts[0].responseId);
      }
      this.$log.debug("[circuit] Fin d'appel");
    }

    if ((this.currentStatus === 'BUSY' || this.currentStatus === 'AWAY' || this.currentStatus === 'DND') && futureStatus === 'AVAILABLE') {
      if (currentStatusContact === AgentStatus.NOT_WORKING) {
        // Set agent available if he's in circuit pause
        this.activityFacade.updateTransitionByType(ActivityType.CIRCUIT, ActivityStatus.FINISHED);
        this.$log.debug('[circuit] pause ended');
      } else {
        if (this.inProgress) {
          // Cancel pause
          this.activityFacade.updateTransitionByType(ActivityType.CIRCUIT, ActivityStatus.CANCELED);
          this.$log.debug('[circuit] pause canceled');
        }
      }
    }
    this.currentStatus = futureStatus;
  }
}

export default MODULE_NAME;
